import {
    type ApiResult,
    apiReturnResults,
    type BaseParams,
    type ConversionRateResponse,
    type EmojiId,
    links,
    type PostParams,
    Request,
    type VisualiserNameParams,
    type VisualizerAllData,
    VisualizerDataKeys,
} from '@app/client';

import { apiFactory } from '../../ApiFactory';

import { type OrdersResponse } from './types';

type SetNameParams = BaseParams & PostParams<VisualiserNameParams> & { eid: EmojiId };
async function setName(params: SetNameParams): Promise<ApiResult<EmojiId>> {
    return Request.postRequest<EmojiId>({
        ...params,
        target: links.visualiser().set(params.eid, VisualizerDataKeys.VisualizerName),
    });
}
export const callSetName = (params: SetNameParams) => apiReturnResults(setName, params);

async function getVisualiserData(
    params: BaseParams & { eid: EmojiId; keys?: VisualizerDataKeys[] },
): Promise<ApiResult<VisualizerAllData>> {
    const keys = Object.values(VisualizerDataKeys).map((k: VisualizerDataKeys) => k); //get all if none set
    return Request.getRequest({
        ...params,
        target: links.visualiser().get(params.eid, params.keys ?? keys),
    });
}

async function getConversionRate(params: BaseParams): Promise<ApiResult<ConversionRateResponse>> {
    const target = links.nft().public.conversionRate;
    return Request.getRequest<ConversionRateResponse>({
        ...params,
        target,
    });
}

export const callGetConversionRate = (params: BaseParams): Promise<ConversionRateResponse> =>
    apiFactory()
        .call(getConversionRate, {
            ...params,
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting conversion rate`;
            throw new Error(errorMessage);
        });

// NFT offers
async function getTokenBids(
    params: BaseParams & { tokenId: string; contract: string },
): Promise<ApiResult<OrdersResponse>> {
    const { tokenId, contract } = params;
    const formattedContract = contract.startsWith('0x') ? contract : `0x${contract}`;
    const target = links.nft().public.bids;

    const token = `${formattedContract}:${tokenId}`;

    return Request.getRequest<OrdersResponse>({
        ...params,
        query: {
            ...params.query,
            token,
        },
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetTokenBids = (params: BaseParams & { tokenId: string; contract: string }): Promise<OrdersResponse> =>
    apiFactory()
        .call(getTokenBids, {
            ...params,
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting token ${params.tokenId}`;
            throw new Error(errorMessage);
        });

export const nftCalls = {
    setName,
    getVisualiserData,
    callGetConversionRate,
    callGetTokenBids,
};
